/* The daily operations report, written from the same readings the dashboard
   shows. It says it is simulated in its own heading and in everything exported
   from it. */

import { clock, dateTime, THRESHOLDS } from './model';

const num = (value, places = 1) => (Number.isFinite(value)
    ? value.toLocaleString('en-US', { minimumFractionDigits: places, maximumFractionDigits: places })
    : '—');

export const REPORT_NOTICE = 'Simulated data. No meters, protection devices, BMS or live data center are connected.';

export const buildReport = (dc, events, alarmLog) => {
    const { A, B } = dc.buses;
    const peakOf = data => data.peakDemandKw;
    const joints = dc.feeders
        .filter(feeder => !feeder.stale)
        .sort((x, y) => y.temp - x.temp)
        .slice(0, 5);

    const critical = dc.conditions.filter(c => c.severity === 'critical').length;

    return {
        title: 'Daily operations report',
        site: 'DC-01 · Data hall 01',
        generated: dateTime(dc.ms),
        period: 'Trailing 24 hours',
        notice: REPORT_NOTICE,
        summary: [
            { label: 'Active alarms', value: `${dc.conditions.length} (${critical} critical)` },
            { label: 'Redundancy', value: dc.redundant ? 'Both paths normal' : 'Reduced' },
            { label: 'Protected DC load', value: `${num(dc.totalKw / 1000, 3)} MW` },
            { label: 'Surviving-path utilization', value: `${num(dc.survivingUse)}%` },
            { label: 'Remaining headroom', value: `${num(dc.headroomKw / 1000, 2)} MW` },
            { label: 'Imported energy', value: `${num(dc.importKwh / 1000, 2)} MWh` },
            { label: 'Distribution efficiency', value: `${num(dc.efficiency, 2)}%` },
            { label: 'Power quality events', value: String(events.length) }
        ],
        paths: [
            { label: 'Imported energy', unit: 'kWh', a: num(A.importKwh), b: num(B.importKwh) },
            { label: '15-minute rolling demand', unit: 'kW', a: num(A.demandKw), b: num(B.demandKw) },
            { label: 'Peak 15-minute demand', unit: 'kW', a: num(peakOf(A)), b: num(peakOf(B)) },
            { label: 'Bus loading', unit: '%', a: num(A.loading), b: num(B.loading) },
            { label: 'Rectifier modules online', unit: 'of 9', a: String(A.rectifier.online), b: String(B.rectifier.online) },
            { label: 'Battery state of charge', unit: '%', a: num(A.battery.soc, 0), b: num(B.battery.soc, 0) },
            { label: 'Runtime at present path load', unit: 'min', a: num(A.battery.runtimeNow), b: num(B.battery.runtimeNow) },
            { label: 'Lowest insulation', unit: 'MΩ', a: num(A.insulation, 2), b: num(B.insulation, 2) }
        ],
        joints: joints.map(feeder => ({
            joint: feeder.joint,
            location: feeder.location,
            temp: `${num(feeder.temp)} °C`,
            rise: `${num(feeder.rise)} K`,
            amps: `${num(feeder.amps)} A`,
            resistance: feeder.rUOhm === null ? '—' : `${num(feeder.rUOhm)} µΩ`
        })),
        events: events.map(event => ({
            time: `${clock(event.at)} ET`,
            type: event.type,
            title: event.title,
            where: `${event.meterLabel} · Path ${event.path}`,
            size: `${num(event.magnitude)}%`,
            duration: `${num(event.duration, event.duration < 1 ? 3 : 1)} ms`
        })),
        alarms: alarmLog.map(row => ({
            time: `${clock(row.at)} ET`,
            asset: row.asset,
            event: row.event,
            state: row.state
        })),
        limits: THRESHOLDS
    };
};

export const reportRows = report => [
    [report.title, report.site],
    ['Generated', report.generated],
    ['Period', report.period],
    ['Data mode', 'SIMULATED'],
    ['Note', report.notice],
    [],
    ['Summary', 'Value'],
    ...report.summary.map(row => [row.label, row.value]),
    [],
    ['Path measurement', 'Path A', 'Path B', 'Unit'],
    ...report.paths.map(row => [row.label, row.a, row.b, row.unit]),
    [],
    ['Hottest joint', 'Location', 'Temperature', 'Rise', 'Load', 'R estimate'],
    ...report.joints.map(row => [row.joint, row.location, row.temp, row.rise, row.amps, row.resistance]),
    [],
    ['Event time', 'Type', 'Event', 'Where', 'Magnitude', 'Duration'],
    ...report.events.map(row => [row.time, row.type, row.title, row.where, row.size, row.duration]),
    [],
    ['Alarm time', 'Asset', 'Alarm', 'State'],
    ...report.alarms.map(row => [row.time, row.asset, row.event, row.state])
];
